// frontend/src/components/SuperAdmin/Dashboard.jsx
import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const SuperAdminDashboard = ({ user, onLogout }) => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Email Settings (Resend + SMTP)
  const [showSmtp, setShowSmtp] = useState(false);
  const [smtpSettings, setSmtpSettings] = useState({ hasResendKey: false, smtpHost: '', smtpPort: 587, smtpSecure: false, smtpEmail: '', hasPassword: false, superAdminEmail: '', appName: 'VoiceApp', appUrl: '' });
  const [smtpForm, setSmtpForm] = useState({ resendApiKey: '', smtpHost: '', smtpPort: 587, smtpSecure: false, smtpEmail: '', smtpPassword: '', superAdminEmail: '', appName: 'VoiceApp', appUrl: '' });
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [smtpTesting, setSmtpTesting] = useState(false);

  // Feature Configuration (separate panel)
  const [showFeatures, setShowFeatures] = useState(false);
  const [featuresForm, setFeaturesForm] = useState({ featureScreenShare: true, featureRemoteControl: false });
  const [featuresSaving, setFeaturesSaving] = useState(false);

  // Create company modal
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', email: '', password: '', maxUsers: 10, maxSessionsPerDay: 50, notes: '' });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  // Edit limits modal
  const [editCompany, setEditCompany] = useState(null);
  const [editForm, setEditForm] = useState({ maxUsers: 10, maxSessionsPerDay: 50, notes: '' });
  const [editing, setEditing] = useState(false);

  // Detail drawer
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Delete company
  const [deleteTarget, setDeleteTarget] = useState(null); // company object
  const [deleteKeyword, setDeleteKeyword] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const [toast, setToast] = useState(null);
  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchCompanies = async () => {
    try {
      const res = await api.get('/super-admin/companies');
      setCompanies(res.data.companies);
    } catch (e) {
      showToast('Failed to load companies', 'error');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchCompanies(); fetchSmtp(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSmtp = async () => {
    try {
      const res = await api.get('/super-admin/smtp');
      setSmtpSettings(res.data);
      setSmtpForm(f => ({
        ...f,
        resendApiKey:    '',                              // never pre-fill — user must re-enter to change
        smtpHost:        res.data.smtpHost        || '',
        smtpPort:        res.data.smtpPort         ?? 587,
        smtpSecure:      res.data.smtpSecure       !== false,
        smtpEmail:       res.data.smtpEmail        || '',
        superAdminEmail: res.data.superAdminEmail  || '',
        appName:         res.data.appName          || 'VoiceApp',
        appUrl:          res.data.appUrl           || '',
      }));
      // Feature flags live in their own form
      setFeaturesForm({
        featureScreenShare:   res.data.featureScreenShare   !== false,
        featureRemoteControl: res.data.featureRemoteControl === true,
      });
    } catch (e) {}
  };

  const handleSaveSmtp = async (e) => {
    e.preventDefault();
    setSmtpSaving(true);
    try {
      await api.post('/super-admin/smtp', smtpForm);
      showToast('SMTP settings saved!');
      fetchSmtp();
    } catch (e) { showToast(e.response?.data?.error || 'Failed to save SMTP', 'error'); }
    finally { setSmtpSaving(false); }
  };

  const handleTestSmtp = async () => {
    setSmtpTesting(true);
    try {
      // Backend reads ONLY from saved DB settings — Save first, then Test
      const res = await api.post('/super-admin/smtp/test');
      showToast(res.data.message, 'success');
    } catch (e) { showToast(e.response?.data?.error || 'Test failed', 'error'); }
    finally { setSmtpTesting(false); }
  };

  const handleSaveFeatures = async (e) => {
    e.preventDefault();
    setFeaturesSaving(true);
    try {
      await api.post('/super-admin/smtp', featuresForm);
      showToast('Feature settings saved!');
      fetchSmtp();
    } catch (e) { showToast(e.response?.data?.error || 'Failed to save features', 'error'); }
    finally { setFeaturesSaving(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true); setCreateError('');
    try {
      await api.post('/super-admin/companies', {
        ...createForm,
        maxUsers: parseInt(createForm.maxUsers),
        maxSessionsPerDay: parseInt(createForm.maxSessionsPerDay)
      });
      showToast('Company created!');
      setShowCreate(false);
      setCreateForm({ name: '', email: '', password: '', maxUsers: 10, maxSessionsPerDay: 50, notes: '' });
      fetchCompanies();
    } catch (e) {
      setCreateError(e.response?.data?.error || 'Failed to create');
    } finally { setCreating(false); }
  };

  const handleToggleActive = async (company) => {
    try {
      const res = await api.patch(`/super-admin/companies/${company.id}/toggle`);
      showToast(res.data.message);
      fetchCompanies();
    } catch (e) { showToast('Failed to toggle status', 'error'); }
  };

  // Toggle a feature flag for a specific company (optimistic UI update)
  const handleToggleFeature = async (company, feature) => {
    // Optimistic update — flip locally first for instant feedback
    setCompanies(prev => prev.map(c => {
      if (c.id !== company.id) return c;
      return { ...c, features: { ...c.features, [feature]: !c.features[feature] } };
    }));
    try {
      const res = await api.patch(`/super-admin/companies/${company.id}/features`, { feature });
      showToast(res.data.message);
    } catch (e) {
      // Revert on error
      setCompanies(prev => prev.map(c => {
        if (c.id !== company.id) return c;
        return { ...c, features: { ...c.features, [feature]: !c.features[feature] } };
      }));
      showToast(e.response?.data?.error || 'Failed to update feature', 'error');
    }
  };

  const openEdit = (company) => {
    setEditCompany(company);
    setEditForm({ maxUsers: company.limits.maxUsers, maxSessionsPerDay: company.limits.maxSessionsPerDay, notes: company.notes || '' });
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setEditing(true);
    try {
      await api.put(`/super-admin/companies/${editCompany.id}`, {
        maxUsers: parseInt(editForm.maxUsers),
        maxSessionsPerDay: parseInt(editForm.maxSessionsPerDay),
        notes: editForm.notes
      });
      showToast('Limits updated!');
      setEditCompany(null);
      fetchCompanies();
    } catch (e) { showToast('Failed to update', 'error'); }
    finally { setEditing(false); }
  };

  const handleDelete = async () => {
    if (deleteKeyword !== 'delete_accepted') {
      setDeleteError('Type exactly: delete_accepted');
      return;
    }
    setDeleting(true);
    setDeleteError('');
    try {
      const res = await api.delete(`/super-admin/companies/${deleteTarget.id}`, {
        data: { confirmation: 'delete_accepted' }
      });
      showToast(res.data.message);
      setDeleteTarget(null);
      setDeleteKeyword('');
      fetchCompanies();
    } catch (e) {
      setDeleteError(e.response?.data?.error || 'Failed to delete company');
    } finally { setDeleting(false); }
  };

  const openDetail = async (company) => {
    setDetail({ company, users: [], recentSessions: [] });
    setDetailLoading(true);
    try {
      const res = await api.get(`/super-admin/companies/${company.id}`);
      setDetail(res.data);
    } catch (e) { showToast('Failed to load detail', 'error'); }
    finally { setDetailLoading(false); }
  };

  const filtered = companies.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.email.toLowerCase().includes(search.toLowerCase())
  );

  const totalStats = {
    companies: companies.length,
    users: companies.reduce((a, c) => a + c.stats.totalUsers, 0),
    sessionsToday: companies.reduce((a, c) => a + c.stats.sessionsToday, 0),
    active: companies.filter(c => c.isActive).length
  };

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-5 py-3 rounded-xl shadow-xl text-sm font-medium ${
          toast.type === 'error' ? 'bg-red-600' : 'bg-green-600'
        }`}>{toast.msg}</div>
      )}

      {/* Nav */}
      <nav className="bg-gray-900 border-b border-gray-800 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-xl p-2">
            <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <div>
            <p className="font-bold text-white">Super Admin</p>
            <p className="text-xs text-gray-400">{user.email}</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button onClick={() => { setShowFeatures(!showFeatures); setShowSmtp(false); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all border ${
              showFeatures
                ? 'border-blue-500 bg-blue-900 text-blue-300'
                : 'border-gray-600 text-gray-400 hover:bg-gray-800'
            }`}>
            ⚙️ Configuration
          </button>
          <button onClick={() => { setShowSmtp(!showSmtp); setShowFeatures(false); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all border ${
              smtpSettings.smtpEmail || smtpSettings.hasResendKey
                ? 'border-green-600 text-green-400 hover:bg-green-900'
                : 'border-yellow-600 text-yellow-400 hover:bg-yellow-900'
            }`}>
            {smtpSettings.hasResendKey ? '✉ Resend Active' : smtpSettings.smtpEmail ? '✉ Email Configured' : '⚠ Setup Email'}
          </button>
          <button onClick={() => setShowCreate(true)}
            className="px-4 py-2 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-lg text-sm font-medium hover:from-red-600 hover:to-orange-600 transition-all">
            + Add Company
          </button>
          <button onClick={onLogout}
            className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg text-sm hover:bg-gray-600 transition-colors">
            Logout
          </button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats bar */}
        <div className="grid grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Companies', value: totalStats.companies, color: 'from-blue-500 to-blue-600' },
            { label: 'Active Companies', value: totalStats.active, color: 'from-green-500 to-emerald-600' },
            { label: 'Total Users', value: totalStats.users, color: 'from-purple-500 to-purple-600' },
            { label: 'Sessions Today', value: totalStats.sessionsToday, color: 'from-orange-500 to-red-500' }
          ].map((s, i) => (
            <div key={i} className={`bg-gradient-to-r ${s.color} rounded-xl p-5 shadow-lg`}>
              <p className="text-sm text-white text-opacity-80">{s.label}</p>
              <p className="text-3xl font-bold text-white mt-1">{s.value}</p>
            </div>
          ))}
        </div>

        {/* SMTP Settings Panel */}
        {showSmtp && (
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 mb-8">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center space-x-3">
                <div className="bg-gradient-to-r from-yellow-500 to-orange-500 rounded-xl p-2">
                  <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-bold">SMTP Email Configuration</h3>
                  <p className="text-gray-400 text-xs">Works with any provider — Hostinger, Gmail, Zoho, SendGrid, Office 365, custom</p>
                </div>
              </div>
              <button onClick={() => setShowSmtp(false)} className="text-gray-500 hover:text-white text-xl leading-none">✕</button>
            </div>

            {/* Provider quick-fill buttons */}
            <div className="mb-5">
              <p className="text-xs text-gray-500 mb-2">Quick fill for common providers:</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { label: 'GoDaddy',   host: 'smtpout.secureserver.net', port: 587, secure: false },
                  { label: 'Hostinger', host: 'smtp.hostinger.com',       port: 587, secure: false },
                  { label: 'Gmail',     host: 'smtp.gmail.com',            port: 465, secure: true  },
                  { label: 'Zoho',      host: 'smtp.zoho.com',             port: 465, secure: true  },
                  { label: 'Outlook',   host: 'smtp.office365.com',        port: 587, secure: false },
                  { label: 'Yahoo',     host: 'smtp.mail.yahoo.com',       port: 465, secure: true  },
                  { label: 'SendGrid',  host: 'smtp.sendgrid.net',         port: 587, secure: false },
                  { label: 'Mailgun',   host: 'smtp.mailgun.org',          port: 587, secure: false },
                ].map(p => (
                  <button key={p.label} type="button"
                    onClick={() => setSmtpForm(f => ({ ...f, smtpHost: p.host, smtpPort: p.port, smtpSecure: p.secure }))}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                      smtpForm.smtpHost === p.host
                        ? 'border-yellow-500 bg-yellow-900 text-yellow-300'
                        : 'border-gray-700 text-gray-400 hover:border-gray-500 hover:text-gray-200'
                    }`}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Gmail note */}
            {smtpForm.smtpHost === 'smtp.gmail.com' && (
              <div className="bg-blue-950 border border-blue-800 rounded-xl p-4 mb-5 text-xs text-blue-300">
                <p className="font-semibold text-blue-200 mb-1">⚠ Gmail requires an App Password (not your regular password)</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Enable 2-Factor Authentication on your Google account</li>
                  <li>Go to <span className="font-mono text-blue-100">myaccount.google.com → Security → App passwords</span></li>
                  <li>Select <strong>Mail → Other</strong> → Generate → copy the 16-char code</li>
                </ol>
              </div>
            )}

            {/* GoDaddy note */}
            {smtpForm.smtpHost === 'smtpout.secureserver.net' && (
              <div className="bg-green-950 border border-green-800 rounded-xl p-4 mb-5 text-xs text-green-300">
                <p className="font-semibold text-green-200 mb-1">ℹ GoDaddy SMTP (STARTTLS)</p>
                <p>Use your GoDaddy Workspace email address and its password. Port 587 with STARTTLS is required — this matches how GoDaddy authenticates.</p>
              </div>
            )}

            {/* Hostinger note */}
            {smtpForm.smtpHost === 'smtp.hostinger.com' && (
              <div className="bg-purple-950 border border-purple-800 rounded-xl p-4 mb-5 text-xs text-purple-300">
                <p className="font-semibold text-purple-200 mb-1">ℹ Hostinger SMTP (STARTTLS)</p>
                <p>Port <strong>587</strong> with STARTTLS is used — this matches Hostinger's recommended setup. Use your full Hostinger email address and its password. Make sure the email account exists in hPanel → Email → Email Accounts.</p>
              </div>
            )}

            {/* ── Resend API banner ── */}
            <div className="bg-indigo-950 border border-indigo-700 rounded-xl p-4 mb-5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <p className="text-indigo-200 font-semibold text-sm mb-1">🚀 Recommended: Resend API <span className="text-indigo-400 font-normal">(bypasses Railway SMTP port blocks)</span></p>
                  <p className="text-indigo-300 text-xs">Railway blocks outbound SMTP. The Resend API sends email over HTTPS — no port issues. Free plan: 3,000 emails/month.</p>
                  <a href="https://resend.com/signup" target="_blank" rel="noreferrer"
                    className="inline-block mt-2 text-xs text-indigo-400 underline hover:text-indigo-200">
                    Sign up free at resend.com →
                  </a>
                </div>
                {smtpSettings.hasResendKey && (
                  <span className="shrink-0 text-xs bg-green-900 text-green-300 px-2 py-1 rounded-full">✓ Active</span>
                )}
              </div>
            </div>

            <form onSubmit={handleSaveSmtp} className="grid grid-cols-2 gap-4">
              {/* Resend API Key */}
              <div className="col-span-2">
                <label className="block text-sm text-gray-400 mb-1">
                  Resend API Key
                  {smtpSettings.hasResendKey
                    ? <span className="ml-2 text-green-400 text-xs">✓ Set — Resend is active (SMTP ignored)</span>
                    : <span className="ml-2 text-gray-500 text-xs">Optional — if blank, SMTP below is used</span>}
                </label>
                <div className="flex gap-2">
                  <input type="password" value={smtpForm.resendApiKey}
                    onChange={e => setSmtpForm(f => ({ ...f, resendApiKey: e.target.value }))}
                    placeholder={smtpSettings.hasResendKey ? 'Leave blank to keep existing key' : 're_xxxxxxxxxxxx  (from resend.com → API Keys)'}
                    className="flex-1 px-3 py-2.5 bg-gray-800 border border-indigo-700 rounded-lg text-white text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                  {smtpSettings.hasResendKey && (
                    <button type="button"
                      onClick={() => setSmtpForm(f => ({ ...f, resendApiKey: 'CLEAR' }))}
                      className="px-3 py-2 text-xs bg-red-900 hover:bg-red-800 text-red-200 rounded-lg transition-colors"
                      title="Remove Resend key and fall back to SMTP">
                      Remove
                    </button>
                  )}
                </div>
                {smtpForm.resendApiKey === 'CLEAR' && (
                  <p className="text-red-400 text-xs mt-1">⚠ Resend key will be removed on Save. SMTP settings below will be used instead.</p>
                )}
              </div>

              <div className="col-span-2 border-t border-gray-700 pt-4">
                <p className="text-gray-500 text-xs mb-3">SMTP settings below are used only when no Resend API key is set:</p>
              </div>

              {/* SMTP Host */}
              <div className="col-span-2">
                <label className="block text-sm text-gray-400 mb-1">SMTP Host</label>
                <input type="text" value={smtpForm.smtpHost}
                  onChange={e => setSmtpForm(f => ({ ...f, smtpHost: e.target.value }))}
                  placeholder="smtp.hostinger.com"
                  className="w-full px-3 py-2.5 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm font-mono focus:outline-none focus:ring-2 focus:ring-yellow-500" />
              </div>

              {/* Port + Security */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Port</label>
                <div className="flex space-x-2">
                  <input type="number" value={smtpForm.smtpPort}
                    onChange={e => setSmtpForm(f => ({ ...f, smtpPort: parseInt(e.target.value) }))}
                    className="w-24 px-3 py-2.5 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm font-mono focus:outline-none focus:ring-2 focus:ring-yellow-500" />
                  <div className="flex items-center space-x-3 ml-2">
                    {[{ val: true, label: '465 SSL', port: 465 }, { val: false, label: '587 TLS', port: 587 }].map(opt => (
                      <label key={String(opt.val)} className="flex items-center space-x-1.5 cursor-pointer">
                        <input type="radio" name="smtpSecure" checked={smtpForm.smtpSecure === opt.val}
                          onChange={() => setSmtpForm(f => ({ ...f, smtpSecure: opt.val, smtpPort: opt.port }))}
                          className="text-yellow-500" />
                        <span className="text-gray-300 text-xs">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Sender email */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Sender Email Address</label>
                <input type="email" value={smtpForm.smtpEmail}
                  onChange={e => setSmtpForm(f => ({ ...f, smtpEmail: e.target.value }))}
                  placeholder="noreply@yourcompany.com"
                  className="w-full px-3 py-2.5 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500" />
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">
                  Password
                  {smtpSettings.hasPassword && <span className="ml-2 text-green-400 text-xs">✓ Set</span>}
                </label>
                <input type="password" value={smtpForm.smtpPassword}
                  onChange={e => setSmtpForm(f => ({ ...f, smtpPassword: e.target.value }))}
                  placeholder={smtpSettings.hasPassword ? 'Leave blank to keep existing' : 'Email account password'}
                  className="w-full px-3 py-2.5 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500" />
              </div>

              {/* Alert email */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">Alert Recipient (new company notifications)</label>
                <input type="email" value={smtpForm.superAdminEmail}
                  onChange={e => setSmtpForm(f => ({ ...f, superAdminEmail: e.target.value }))}
                  placeholder="admin@yourcompany.com"
                  className="w-full px-3 py-2.5 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500" />
              </div>

              {/* App Name */}
              <div>
                <label className="block text-sm text-gray-400 mb-1">App Name (in email subject/body)</label>
                <input type="text" value={smtpForm.appName}
                  onChange={e => setSmtpForm(f => ({ ...f, appName: e.target.value }))}
                  placeholder="VoiceApp"
                  className="w-full px-3 py-2.5 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500" />
              </div>

              {/* App URL */}
              <div className="col-span-2">
                <label className="block text-sm text-gray-400 mb-1">App URL (login button in activation emails)</label>
                <input type="text" value={smtpForm.appUrl}
                  onChange={e => setSmtpForm(f => ({ ...f, appUrl: e.target.value }))}
                  placeholder="https://yoursite.com"
                  className="w-full px-3 py-2.5 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500" />
              </div>

              {/* Actions */}
              <div className="col-span-2 flex space-x-3">
                <button type="submit" disabled={smtpSaving}
                  className="flex-1 py-2.5 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-lg font-medium hover:from-yellow-600 hover:to-orange-600 disabled:opacity-60 text-sm transition-all">
                  {smtpSaving ? 'Saving...' : 'Save SMTP Settings'}
                </button>
                <button type="button" onClick={handleTestSmtp}
                  disabled={smtpTesting || !smtpForm.smtpEmail || (!smtpSettings.hasResendKey && !smtpForm.smtpHost) || (!smtpSettings.hasResendKey && !smtpForm.smtpPassword && !smtpSettings.hasPassword)}
                  title={smtpSettings.hasResendKey ? 'Send test via Resend API' : 'Send test via SMTP — Save settings first'}
                  className="px-5 py-2.5 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg text-sm disabled:opacity-40 transition-colors">
                  {smtpTesting ? 'Sending...' : smtpSettings.hasResendKey ? '📧 Test via Resend' : '📧 Test via SMTP'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* ── Features Configuration Panel ───────────────────────────────── */}
        {showFeatures && (
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 mb-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl p-2">
                  <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-bold">Global Feature Defaults</h3>
                  <p className="text-gray-400 text-xs">Default fallback only — individual company feature access is controlled per-company in the table below</p>
                </div>
              </div>
              <button onClick={() => setShowFeatures(false)} className="text-gray-500 hover:text-white text-xl leading-none">✕</button>
            </div>

            <form onSubmit={handleSaveFeatures} className="space-y-3">

              {/* Screen Share */}
              <div className="flex items-center justify-between p-4 bg-gray-800 border border-gray-700 rounded-xl hover:border-gray-600 transition-colors">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 p-1.5 bg-blue-900 rounded-lg flex-shrink-0">
                    <svg className="h-4 w-4 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-2" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">Screen Share</p>
                    <p className="text-gray-500 text-xs mt-0.5">Participants can share their screen inside a room. Host sees it live via WebRTC (peer-to-peer, no server relay needed).</p>
                    <p className="text-blue-400 text-xs mt-1 font-mono">GET /api/settings/features → screenShare</p>
                  </div>
                </div>
                <div className="relative ml-6 flex-shrink-0">
                  <div onClick={() => setFeaturesForm(f => ({ ...f, featureScreenShare: !f.featureScreenShare }))}
                    className={`w-12 h-7 rounded-full cursor-pointer transition-all duration-200 ${featuresForm.featureScreenShare ? 'bg-blue-500' : 'bg-gray-600'}`}>
                    <div className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow-md transition-all duration-200 ${featuresForm.featureScreenShare ? 'left-6' : 'left-1'}`} />
                  </div>
                  <p className={`text-center text-xs mt-1 font-medium ${featuresForm.featureScreenShare ? 'text-blue-400' : 'text-gray-500'}`}>
                    {featuresForm.featureScreenShare ? 'ON' : 'OFF'}
                  </p>
                </div>
              </div>

              {/* Remote Control */}
              <div className="flex items-center justify-between p-4 bg-gray-800 border border-gray-700 rounded-xl hover:border-gray-600 transition-colors">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 p-1.5 bg-orange-900 rounded-lg flex-shrink-0">
                    <svg className="h-4 w-4 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">Remote Control</p>
                    <p className="text-gray-500 text-xs mt-0.5">Host can request control of a participant's shared screen. Participant must approve. Browser-level only — works for web page elements, not OS-level apps.</p>
                    <p className="text-orange-400 text-xs mt-1 font-mono">GET /api/settings/features → remoteControl</p>
                  </div>
                </div>
                <div className="relative ml-6 flex-shrink-0">
                  <div onClick={() => setFeaturesForm(f => ({ ...f, featureRemoteControl: !f.featureRemoteControl }))}
                    className={`w-12 h-7 rounded-full cursor-pointer transition-all duration-200 ${featuresForm.featureRemoteControl ? 'bg-orange-500' : 'bg-gray-600'}`}>
                    <div className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow-md transition-all duration-200 ${featuresForm.featureRemoteControl ? 'left-6' : 'left-1'}`} />
                  </div>
                  <p className={`text-center text-xs mt-1 font-medium ${featuresForm.featureRemoteControl ? 'text-orange-400' : 'text-gray-500'}`}>
                    {featuresForm.featureRemoteControl ? 'ON' : 'OFF'}
                  </p>
                </div>
              </div>

              {/* Current state summary */}
              <div className="p-3 bg-gray-850 border border-gray-700 rounded-lg text-xs text-gray-500 font-mono">
                Active: screenShare={String(featuresForm.featureScreenShare)}, remoteControl={String(featuresForm.featureRemoteControl)}
              </div>

              <div className="flex justify-end pt-2">
                <button type="submit" disabled={featuresSaving}
                  className="px-6 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg font-medium hover:from-blue-600 hover:to-indigo-700 disabled:opacity-60 text-sm transition-all">
                  {featuresSaving ? 'Saving...' : '💾 Save Feature Settings'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Search */}
        <div className="mb-6">
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search companies by name or email..."
            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500" />
        </div>

        {/* Companies Table */}
        <div className="bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-800">
            <h2 className="text-lg font-bold text-white">Companies ({filtered.length})</h2>
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin h-10 w-10 rounded-full border-4 border-orange-500 border-t-transparent" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-gray-500">No companies found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-800">
                    {['Company', 'Email', 'Users', 'Sessions Today', 'Limits', 'Features', 'Status', 'Actions'].map(h => (
                      <th key={h} className="px-5 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {filtered.map(c => (
                    <tr key={c.id} className="hover:bg-gray-800 transition-colors">
                      <td className="px-5 py-4">
                        <div>
                          <p className="font-medium text-white">{c.name}</p>
                          <p className="text-xs text-gray-500">{new Date(c.createdAt).toLocaleDateString()}</p>
                          {c.notes && <p className="text-xs text-gray-600 mt-0.5 italic">{c.notes}</p>}
                        </div>
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-300">{c.email}</td>
                      <td className="px-5 py-4">
                        <div className="flex items-center space-x-2">
                          <span className="text-white font-medium">{c.stats.totalUsers}</span>
                          <span className="text-gray-500">/</span>
                          <span className="text-gray-400">{c.limits.maxUsers}</span>
                          <div className="w-16 bg-gray-700 rounded-full h-1.5 ml-1">
                            <div className={`h-1.5 rounded-full ${
                              c.stats.totalUsers / c.limits.maxUsers > 0.8 ? 'bg-red-500' : 'bg-green-500'
                            }`} style={{ width: `${Math.min(100, (c.stats.totalUsers / c.limits.maxUsers) * 100)}%` }} />
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center space-x-2">
                          <span className="text-white font-medium">{c.stats.sessionsToday}</span>
                          <span className="text-gray-500">/</span>
                          <span className="text-gray-400">{c.limits.maxSessionsPerDay}</span>
                          <div className="w-16 bg-gray-700 rounded-full h-1.5 ml-1">
                            <div className={`h-1.5 rounded-full ${
                              c.stats.sessionsToday / c.limits.maxSessionsPerDay > 0.8 ? 'bg-red-500' : 'bg-blue-500'
                            }`} style={{ width: `${Math.min(100, (c.stats.sessionsToday / c.limits.maxSessionsPerDay) * 100)}%` }} />
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-xs text-gray-400">
                        <div>{c.limits.maxUsers} users</div>
                        <div>{c.limits.maxSessionsPerDay} sess/day</div>
                      </td>
                      {/* Per-company feature toggles */}
                      <td className="px-5 py-4">
                        <div className="flex flex-col gap-1.5">
                          {/* Screen Share toggle */}
                          <button
                            onClick={() => handleToggleFeature(c, 'screenShare')}
                            title={`Screen Share: ${c.features?.screenShare ? 'ON — click to disable' : 'OFF — click to enable'}`}
                            className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium border transition-all ${
                              c.features?.screenShare
                                ? 'bg-blue-900 border-blue-600 text-blue-300 hover:bg-blue-800'
                                : 'bg-gray-800 border-gray-700 text-gray-500 hover:border-gray-500'
                            }`}
                          >
                            <svg className="h-3 w-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-2" />
                            </svg>
                            <span>Screen {c.features?.screenShare ? 'ON' : 'OFF'}</span>
                          </button>
                          {/* Remote Control toggle */}
                          <button
                            onClick={() => handleToggleFeature(c, 'remoteControl')}
                            title={`Remote Control: ${c.features?.remoteControl ? 'ON — click to disable' : 'OFF — click to enable'}`}
                            className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium border transition-all ${
                              c.features?.remoteControl
                                ? 'bg-orange-900 border-orange-600 text-orange-300 hover:bg-orange-800'
                                : 'bg-gray-800 border-gray-700 text-gray-500 hover:border-gray-500'
                            }`}
                          >
                            <svg className="h-3 w-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5" />
                            </svg>
                            <span>Remote {c.features?.remoteControl ? 'ON' : 'OFF'}</span>
                          </button>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                          c.isActive ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300'
                        }`}>
                          {c.isActive ? '● Active' : '● Suspended'}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center space-x-2">
                          <button onClick={() => openDetail(c)}
                            className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-xs text-gray-200 rounded-lg transition-colors">
                            View
                          </button>
                          <button onClick={() => openEdit(c)}
                            className="px-3 py-1.5 bg-blue-800 hover:bg-blue-700 text-xs text-blue-200 rounded-lg transition-colors">
                            Limits
                          </button>
                          <button onClick={() => handleToggleActive(c)}
                            className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
                              c.isActive
                                ? 'bg-yellow-900 hover:bg-yellow-800 text-yellow-200'
                                : 'bg-green-900 hover:bg-green-800 text-green-200'
                            }`}>
                            {c.isActive ? 'Suspend' : 'Activate'}
                          </button>
                          <button
                            onClick={() => { setDeleteTarget(c); setDeleteKeyword(''); setDeleteError(''); }}
                            className="px-3 py-1.5 bg-red-950 hover:bg-red-900 text-xs text-red-300 rounded-lg transition-colors border border-red-800">
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Create Company Modal ── */}
      {showCreate && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 px-4">
          <div className="bg-gray-900 rounded-2xl border border-gray-700 shadow-2xl p-8 w-full max-w-lg">
            <h3 className="text-xl font-bold text-white mb-6">Create New Company</h3>
            {createError && (
              <div className="mb-4 bg-red-900 bg-opacity-50 border border-red-700 rounded-lg p-3">
                <p className="text-red-300 text-sm">{createError}</p>
              </div>
            )}
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm text-gray-400 mb-1">Company Name</label>
                  <input type="text" required value={createForm.name}
                    onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full px-4 py-2.5 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm text-gray-400 mb-1">Email</label>
                  <input type="email" required value={createForm.email}
                    onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))}
                    className="w-full px-4 py-2.5 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm text-gray-400 mb-1">Password</label>
                  <input type="password" required value={createForm.password}
                    onChange={e => setCreateForm(f => ({ ...f, password: e.target.value }))}
                    className="w-full px-4 py-2.5 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500" />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Max Users</label>
                  <input type="number" min="1" max="1000" value={createForm.maxUsers}
                    onChange={e => setCreateForm(f => ({ ...f, maxUsers: e.target.value }))}
                    className="w-full px-4 py-2.5 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500" />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Session Keys / Day</label>
                  <input type="number" min="1" max="10000" value={createForm.maxSessionsPerDay}
                    onChange={e => setCreateForm(f => ({ ...f, maxSessionsPerDay: e.target.value }))}
                    className="w-full px-4 py-2.5 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm text-gray-400 mb-1">Notes (optional)</label>
                  <input type="text" value={createForm.notes}
                    onChange={e => setCreateForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="e.g. Trial plan, contacted via LinkedIn..."
                    className="w-full px-4 py-2.5 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500" />
                </div>
              </div>
              <div className="flex space-x-3 pt-2">
                <button type="submit" disabled={creating}
                  className="flex-1 py-2.5 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-lg font-medium hover:from-red-600 hover:to-orange-600 disabled:opacity-60">
                  {creating ? 'Creating...' : 'Create Company'}
                </button>
                <button type="button" onClick={() => { setShowCreate(false); setCreateError(''); }}
                  className="px-5 py-2.5 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Edit Limits Modal ── */}
      {editCompany && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 px-4">
          <div className="bg-gray-900 rounded-2xl border border-gray-700 shadow-2xl p-8 w-full max-w-md">
            <h3 className="text-xl font-bold text-white mb-1">Edit Limits</h3>
            <p className="text-gray-400 text-sm mb-6">{editCompany.name}</p>
            <form onSubmit={handleEdit} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Max Users</label>
                <input type="number" min="1" max="10000" value={editForm.maxUsers}
                  onChange={e => setEditForm(f => ({ ...f, maxUsers: e.target.value }))}
                  className="w-full px-4 py-2.5 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
                <p className="text-xs text-gray-500 mt-1">Currently {editCompany.stats.totalUsers} users</p>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Session Keys per Day</label>
                <input type="number" min="1" max="100000" value={editForm.maxSessionsPerDay}
                  onChange={e => setEditForm(f => ({ ...f, maxSessionsPerDay: e.target.value }))}
                  className="w-full px-4 py-2.5 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
                <p className="text-xs text-gray-500 mt-1">{editCompany.stats.sessionsToday} generated today</p>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Notes</label>
                <input type="text" value={editForm.notes}
                  onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                  className="w-full px-4 py-2.5 bg-gray-800 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="flex space-x-3 pt-2">
                <button type="submit" disabled={editing}
                  className="flex-1 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium disabled:opacity-60">
                  {editing ? 'Saving...' : 'Save Limits'}
                </button>
                <button type="button" onClick={() => setEditCompany(null)}
                  className="px-5 py-2.5 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Delete Confirmation Modal ── */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 px-4">
          <div className="bg-gray-900 border border-red-800 rounded-2xl shadow-2xl p-8 w-full max-w-md">
            {/* Warning header */}
            <div className="flex items-center space-x-3 mb-5">
              <div className="bg-red-900 rounded-full p-3 flex-shrink-0">
                <svg className="h-7 w-7 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-red-400">Permanent Deletion</h3>
                <p className="text-gray-400 text-sm">This cannot be undone</p>
              </div>
            </div>

            {/* What gets deleted */}
            <div className="bg-red-950 border border-red-800 rounded-xl p-4 mb-5 space-y-1 text-sm">
              <p className="text-red-200 font-semibold mb-2">
                The following will be permanently erased for <span className="text-white">{deleteTarget.name}</span>:
              </p>
              {[
                `All ${deleteTarget.stats?.totalUsers ?? 0} user accounts`,
                'All session keys (active and expired)',
                'All transcripts and recordings',
                'All room history',
                'The company account itself'
              ].map((item, i) => (
                <div key={i} className="flex items-center space-x-2 text-red-300">
                  <span className="text-red-500">✗</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>

            {/* Keyword input */}
            <div className="mb-5">
              <label className="block text-sm text-gray-300 mb-2">
                Type <span className="font-mono text-red-400 bg-gray-800 px-1.5 py-0.5 rounded">delete_accepted</span> to confirm
              </label>
              <input
                type="text"
                value={deleteKeyword}
                onChange={e => { setDeleteKeyword(e.target.value); setDeleteError(''); }}
                onKeyDown={e => e.key === 'Enter' && handleDelete()}
                placeholder="delete_accepted"
                autoFocus
                className={`w-full px-4 py-3 rounded-lg font-mono text-sm focus:outline-none focus:ring-2 bg-gray-800 text-white border ${
                  deleteKeyword === 'delete_accepted'
                    ? 'border-red-500 focus:ring-red-600'
                    : 'border-gray-600 focus:ring-gray-500'
                }`}
              />
              {deleteError && (
                <p className="text-red-400 text-xs mt-1.5">{deleteError}</p>
              )}
            </div>

            {/* Actions */}
            <div className="flex space-x-3">
              <button
                onClick={handleDelete}
                disabled={deleting || deleteKeyword !== 'delete_accepted'}
                className="flex-1 py-3 bg-red-700 hover:bg-red-600 text-white font-bold rounded-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {deleting ? 'Deleting...' : 'Delete Everything'}
              </button>
              <button
                onClick={() => { setDeleteTarget(null); setDeleteKeyword(''); setDeleteError(''); }}
                disabled={deleting}
                className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-xl transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Company Detail Drawer ── */}
      {detail && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-end z-50" onClick={() => setDetail(null)}>
          <div className="w-full max-w-2xl bg-gray-900 border-l border-gray-800 h-full overflow-y-auto"
            onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-800 flex items-start justify-between">
              <div>
                <h3 className="text-xl font-bold text-white">{detail.company?.name}</h3>
                <p className="text-gray-400 text-sm">{detail.company?.email}</p>
              </div>
              <button onClick={() => setDetail(null)} className="text-gray-500 hover:text-white p-1">✕</button>
            </div>

            {detailLoading ? (
              <div className="flex justify-center py-16">
                <div className="animate-spin h-8 w-8 rounded-full border-4 border-orange-500 border-t-transparent" />
              </div>
            ) : (
              <div className="p-6 space-y-8">
                {/* Users */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
                    Users ({detail.users?.length})
                  </h4>
                  <div className="space-y-2">
                    {detail.users?.map(u => (
                      <div key={u.id} className="flex items-center justify-between bg-gray-800 rounded-lg px-4 py-3">
                        <div>
                          <p className="text-white text-sm font-medium">{u.first_name} {u.last_name}</p>
                          <p className="text-gray-500 text-xs">{u.email}</p>
                        </div>
                        <div className="text-right text-xs text-gray-500">
                          <p>{u.total_sessions} sessions</p>
                          <p>{u.total_transcripts} transcripts</p>
                        </div>
                      </div>
                    ))}
                    {!detail.users?.length && <p className="text-gray-600 text-sm">No users yet</p>}
                  </div>
                </div>

                {/* Recent Sessions */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
                    Recent Session Keys (last 20)
                  </h4>
                  <div className="space-y-2">
                    {detail.recentSessions?.map(s => (
                      <div key={s.id} className="flex items-center justify-between bg-gray-800 rounded-lg px-4 py-3">
                        <div>
                          <p className="text-white text-sm font-medium">{s.first_name} {s.last_name}</p>
                          <p className="text-gray-500 text-xs font-mono">{s.session_key?.slice(0, 16)}…</p>
                        </div>
                        <div className="text-right text-xs">
                          <p className={s.is_active ? 'text-green-400' : 'text-red-400'}>
                            {s.is_active ? 'Active' : 'Inactive'}
                          </p>
                          <p className="text-gray-500">{new Date(s.created_at).toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                    {!detail.recentSessions?.length && <p className="text-gray-600 text-sm">No sessions yet</p>}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboard;
